import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ExternalLink, ArrowRight, Mail, Phone, Calendar, Users, CalendarDays, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import {
  getLeads, createLead, updateLead, deleteLead, convertLead, LEAD_STATUSES, fmtDate,
} from "@/lib/api";
import AnnualCalendar from "@/components/AnnualCalendar";

// Status -> column styling (kept restrained — board is busy enough without
// rainbow chrome).
const COL_STYLES = {
  new_inquiry:      "border-t-slate-400",
  info_sent:        "border-t-indigo-400",
  waiting_reply:    "border-t-amber-400",
  discussing_terms: "border-t-sky-500",
  interested:       "border-t-violet-400",
  deposit_pending:  "border-t-orange-400",
  confirmed:        "border-t-emerald-500",
  lost:             "border-t-slate-300",
};
const COL_BG = {
  confirmed: "bg-emerald-50/30",
  lost: "bg-slate-50/60",
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// Days since the given ISO date string (positive integer, 0 if same day).
const daysSince = (iso) => {
  if (!iso) return null;
  const m = String(iso).slice(0, 10);
  const t = new Date(m + "T00:00:00").getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  return Math.max(0, Math.floor(diff / 86400000));
};

// Returns the contact-freshness dot styling for a lead.
const freshness = (lastContactIso) => {
  const d = daysSince(lastContactIso);
  if (d === null) return { color: "bg-slate-300", label: "Never contacted" };
  if (d >= 7) return { color: "bg-red-500", label: `${d}d since contact` };
  if (d >= 3) return { color: "bg-amber-400", label: `${d}d since contact` };
  return { color: "bg-emerald-500", label: d === 0 ? "Contacted today" : `${d}d since contact` };
};

const emptyDraft = {
  name: "", email: "", phone: "", brand: "", source: "",
  interested_dates: "", participants: "", notes: "", email_thread_link: "",
  status: "new_inquiry",
  inquiry_date: todayIso(), last_contact_date: todayIso(), deposit_date: "",
};

export default function Leads() {
  const [tab, setTab] = useState("pipeline"); // "pipeline" | "calendar"
  const [leads, setLeads] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editing, setEditing] = useState(null); // lead being edited
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try { setLeads(await getLeads()); }
    catch { toast.error("Failed to load leads"); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(LEAD_STATUSES.map(s => [s.value, []]));
    for (const lead of leads) {
      if (!g[lead.status]) g[lead.status] = [];
      g[lead.status].push(lead);
    }
    return g;
  }, [leads]);

  const handleCreate = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    try {
      await createLead({ ...draft, name: draft.name.trim() });
      toast.success("Lead added");
      setAddOpen(false);
      setDraft(emptyDraft);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add lead"); }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) return toast.error("Name required");
    try {
      await updateLead(editing.id, {
        name: editing.name.trim(),
        email: editing.email || "",
        phone: editing.phone || "",
        brand: editing.brand || "",
        source: editing.source || "",
        interested_dates: editing.interested_dates || "",
        participants: String(editing.participants || ""),
        notes: editing.notes || "",
        email_thread_link: editing.email_thread_link || "",
        status: editing.status,
        inquiry_date: editing.inquiry_date || "",
        last_contact_date: editing.last_contact_date || "",
        deposit_date: editing.deposit_date || "",
      });
      toast.success("Lead updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update lead"); }
  };

  const handleStatusChange = async (lead, newStatus) => {
    if (lead.status === newStatus) return;
    try {
      const patch = { status: newStatus, last_contact_date: todayIso() };
      if (newStatus === "deposit_pending" && !lead.deposit_date) {
        patch.deposit_date = todayIso();
      }
      await updateLead(lead.id, patch);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to move lead"); }
  };

  const handleConvert = async (lead) => {
    try {
      const res = await convertLead(lead.id);
      if (res.already_converted) {
        toast.info(`Already converted to partner "${res.partner.name}"`);
      } else {
        toast.success(`Converted — new partner "${res.partner.name}" created`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to convert"); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteLead(confirmDelete.id);
      toast.success("Lead removed");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  // --- Drag & drop ---
  const [dragId, setDragId] = useState(null);
  const onDragStart = (id) => (e) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const onDragEnd = () => setDragId(null);
  const onColDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onColDrop = (status) => (e) => {
    e.preventDefault();
    if (!dragId) return;
    const lead = leads.find(l => l.id === dragId);
    if (!lead) return;
    handleStatusChange(lead, status);
    setDragId(null);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5" data-testid="leads-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">
            {tab === "pipeline"
              ? "Potential partners pipeline · drag cards between stages"
              : "Annual retreat planning · months across, days down"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Tab toggle */}
          <div className="inline-flex items-center gap-0.5 border border-slate-200 rounded-md p-0.5 bg-white" data-testid="leads-tabs">
            <button
              onClick={() => setTab("pipeline")}
              className={`h-9 px-3 inline-flex items-center gap-1.5 rounded text-sm font-medium ${tab === "pipeline" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="leads-tab-pipeline"
            >
              <LayoutGrid size={14} /> Pipeline
            </button>
            <button
              onClick={() => setTab("calendar")}
              className={`h-9 px-3 inline-flex items-center gap-1.5 rounded text-sm font-medium ${tab === "calendar" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="leads-tab-calendar"
            >
              <CalendarDays size={14} /> Annual Calendar
            </button>
          </div>
          {tab === "pipeline" && (
            <Button onClick={() => { setDraft({ ...emptyDraft, inquiry_date: todayIso(), last_contact_date: todayIso() }); setAddOpen(true); }} className="h-10 bg-blue-600 hover:bg-blue-700 text-white" data-testid="leads-add-btn">
              <Plus size={14} /> New lead
            </Button>
          )}
        </div>
      </div>

      {tab === "calendar" ? (
        <AnnualCalendar />
      ) : (
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max" data-testid="leads-board">
          {LEAD_STATUSES.map(s => (
            <div
              key={s.value}
              className={`w-[280px] shrink-0 bg-white rounded-md border border-slate-200 border-t-4 ${COL_STYLES[s.value] || ""} ${COL_BG[s.value] || ""}`}
              onDragOver={onColDragOver}
              onDrop={onColDrop(s.value)}
              data-testid={`leads-col-${s.value}`}
            >
              <div className="px-3 py-2.5 border-b border-slate-100 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">{s.label}</span>
                <span className="text-xs text-slate-400 tabular-nums" data-testid={`leads-col-count-${s.value}`}>{(grouped[s.value] || []).length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px]">
                {(grouped[s.value] || []).length === 0 && (
                  <div className="text-xs text-slate-300 italic px-2 py-3 text-center">Drop here</div>
                )}
                {(grouped[s.value] || []).map(lead => {
                  const f = freshness(lead.last_contact_date);
                  return (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={onDragStart(lead.id)}
                      onDragEnd={onDragEnd}
                      className={`bg-white border border-slate-200 hover:border-slate-300 rounded-md p-2.5 cursor-grab active:cursor-grabbing shadow-sm hover:shadow transition-shadow ${dragId === lead.id ? "opacity-50" : ""}`}
                      data-testid={`lead-card-${lead.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm text-slate-900 truncate flex items-center gap-1.5">
                            <span title={f.label} className={`inline-block h-2 w-2 rounded-full shrink-0 ${f.color}`} data-testid={`lead-dot-${lead.id}`} />
                            <span className="truncate">{lead.name}</span>
                          </div>
                          {lead.brand && <div className="text-xs text-slate-600 mt-0.5 truncate">{lead.brand}</div>}
                        </div>
                        <div className="flex items-center gap-0.5">
                          <button onClick={() => setEditing({ ...lead })} className="p-1 text-slate-300 hover:text-slate-700" title="Edit" data-testid={`lead-edit-${lead.id}`}>
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => setConfirmDelete(lead)} className="p-1 text-slate-300 hover:text-red-600" title="Delete" data-testid={`lead-delete-${lead.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 mt-1.5 text-[11px] text-slate-500 flex-wrap">
                        {lead.interested_dates && <span className="flex items-center gap-1"><Calendar size={10} /> {lead.interested_dates}</span>}
                        {lead.participants && <span className="flex items-center gap-1"><Users size={10} /> {lead.participants}</span>}
                        {lead.source && <span className="text-slate-400">· {lead.source}</span>}
                      </div>
                      <div className="mt-1.5 text-[10px] text-slate-400" title={`Last contact: ${fmtDate(lead.last_contact_date) || "never"}`}>
                        {f.label}
                      </div>
                      {s.value === "confirmed" && (
                        <Button
                          size="sm"
                          onClick={() => handleConvert(lead)}
                          className="mt-2 h-7 w-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`lead-convert-${lead.id}`}
                        >
                          {lead.converted_partner_id ? "Already a Partner" : <><ArrowRight size={11} /> Convert to Partner</>}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* New lead */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="leads-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Lead</DialogTitle>
            <DialogDescription className="sr-only">Capture a potential retreat organizer.</DialogDescription>
          </DialogHeader>
          <LeadForm value={draft} onChange={setDraft} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="leads-add-save">Add lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit lead */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="lead-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Lead</DialogTitle>
            <DialogDescription className="sr-only">Update lead details.</DialogDescription>
          </DialogHeader>
          {editing && <LeadForm value={editing} onChange={setEditing} showStatus />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="lead-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md" data-testid="lead-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete lead?</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              This will remove <span className="font-semibold text-slate-900">{confirmDelete?.name}</span>. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="lead-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadForm({ value, onChange, showStatus }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Name *</Label>
          <Input value={value.name} onChange={(e) => set("name", e.target.value)} className="mt-1 h-10" autoFocus data-testid="lead-form-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Brand / Retreat name</Label>
          <Input value={value.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1 h-10" data-testid="lead-form-brand" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Mail size={10} /> Email</Label>
          <Input value={value.email} onChange={(e) => set("email", e.target.value)} className="mt-1 h-10" data-testid="lead-form-email" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Phone size={10} /> Phone</Label>
          <Input value={value.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 h-10" data-testid="lead-form-phone" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Interested dates</Label>
          <Input value={value.interested_dates} placeholder="e.g. May 2026 or 12–18 May" onChange={(e) => set("interested_dates", e.target.value)} className="mt-1 h-10" data-testid="lead-form-dates" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500"># Participants</Label>
          <Input value={value.participants || ""} placeholder="e.g. 8-12 or around 10" onChange={(e) => set("participants", e.target.value)} className="mt-1 h-10" data-testid="lead-form-participants" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Source <span className="text-slate-300 normal-case tracking-normal">(how they found us)</span></Label>
        <Input value={value.source || ""} placeholder="e.g. Instagram, Referral, Booking.com, Word of mouth…" onChange={(e) => set("source", e.target.value)} className="mt-1 h-10" data-testid="lead-form-source" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><ExternalLink size={10} /> Email thread link</Label>
        <Input value={value.email_thread_link} placeholder="paste Gmail / Outlook conversation URL" onChange={(e) => set("email_thread_link", e.target.value)} className="mt-1 h-10" data-testid="lead-form-thread" />
        {value.email_thread_link && (
          <a href={value.email_thread_link} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1">
            Open thread <ExternalLink size={10} />
          </a>
        )}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
        <Textarea value={value.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 min-h-[60px]" data-testid="lead-form-notes" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Inquiry date</Label>
          <Input type="date" value={value.inquiry_date || ""} onChange={(e) => set("inquiry_date", e.target.value)} className="mt-1 h-10" data-testid="lead-form-inquiry-date" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Last contact</Label>
          <Input type="date" value={value.last_contact_date || ""} onChange={(e) => set("last_contact_date", e.target.value)} className="mt-1 h-10" data-testid="lead-form-last-contact" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Deposit date</Label>
          <Input type="date" value={value.deposit_date || ""} onChange={(e) => set("deposit_date", e.target.value)} className="mt-1 h-10" data-testid="lead-form-deposit-date" />
        </div>
      </div>
      {showStatus && (
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
          <select value={value.status} onChange={(e) => set("status", e.target.value)} className="mt-1 h-10 w-full border border-slate-200 rounded-md bg-white px-3 text-sm" data-testid="lead-form-status">
            {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
